import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import {
  SubscriptionNotificationActionType,
  SubscriptionNotificationStatus,
  SubscriptionUserNotificationGQL,
  SubscriptionUserNotificationSubscription,
} from '../../../graphql/generated-subscription';
import { GraphqlWsLifecycleService } from '../../../gql-apollo/graphql-ws-lifecycle.service';
import { ReconnectingGraphqlSubscription } from '../../../gql-apollo/reconnecting-graphql-subscription';
import { ToastMessageService } from '../toast-message.service';
import { FileHandlingService } from '../files/file-handling.service';
import {
  NotificationToastContent,
  NotificationToastLine,
} from '@agility-components/elements/agility-toast/agility-toast.model';
import { TranslateService } from '@ngx-translate/core';

export type UserNotification =
  SubscriptionUserNotificationSubscription['userNotification'];

export const COPY_FAILED_IDS_ACTION_ID = 'copy-failed-ids';
export const DOWNLOAD_FILE_ACTION_ID = 'download-file';

const COPY_FAILED_IDS_TOOLTIP = 'Copy loan numbers';
const MAX_INLINE_FAILURES = 100;
const USER_NOTIFICATION_SUBSCRIPTION_SCOPE = 'userNotification';

interface NotificationFailure {
  loanId?: string;
  reason?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserNotificationService {
  private readonly notificationMap = new Map<string, UserNotification>();

  private wantsConnection = false;
  private sessionActive = false;

  private readonly liveSubscription: ReconnectingGraphqlSubscription<UserNotification>;

  constructor(
    private readonly subscriptionUserNotificationGQL: SubscriptionUserNotificationGQL,
    wsLifecycle: GraphqlWsLifecycleService,
    private readonly toastMessageService: ToastMessageService,
    private readonly router: Router,
    private readonly fileHandlingService: FileHandlingService,
    private readonly translate: TranslateService
  ) {
    this.liveSubscription = new ReconnectingGraphqlSubscription(
      {
        scope: USER_NOTIFICATION_SUBSCRIPTION_SCOPE,
        shouldRun: () => this.wantsConnection && this.sessionActive,
        createStream: () => this.createNotificationStream(),
        onValue: (notification) => this.processLiveNotification(notification),
      },
      wsLifecycle
    );
  }

  setSessionActive(active: boolean): void {
    this.sessionActive = active;
    if (!active) {
      this.disconnect();
    }
  }

  connect(): void {
    this.wantsConnection = true;
    this.liveSubscription.start();
  }

  disconnect(): void {
    this.wantsConnection = false;
    this.liveSubscription.stop();
    this.notificationMap.clear();
  }

  handleNotification(notification: UserNotification): void {
    const content = this.buildNotificationToastContent(notification);
    const sticky =
      content.severity !== 'info' ||
      this.getInlineFailures(notification).length > 0 ||
      this.shouldShowDownloadAction(notification);

    this.toastMessageService.showNotificationToast(
      notification.notificationId,
      content,
      sticky
    );
  }

  dispatchToastAction(notificationId: string, actionId: string): void {
    const notification = this.notificationMap.get(notificationId);
    if (!notification) {
      return;
    }

    switch (actionId) {
      case COPY_FAILED_IDS_ACTION_ID:
        this.copyFailedLoanIds(notification);
        break;
      case DOWNLOAD_FILE_ACTION_ID:
        this.executeAction(notification.action, notification);
        break;
      default:
        break;
    }
  }

  buildNotificationToastContent(
    notification: UserNotification
  ): NotificationToastContent {
    const severity = this.getToastSeverity(notification.status);
    const lines = this.buildNotificationToastLines(notification);

    return {
      severity,
      title: notification.title,
      summary: notification.message,
      lines: lines.length > 0 ? lines : undefined,
      linkAction: this.shouldShowDownloadAction(notification)
        ? {
            actionId: DOWNLOAD_FILE_ACTION_ID,
            label: this.translate.instant('notifications.actions.downloadFile'),
          }
        : undefined,
    };
  }

  private createNotificationStream(): Observable<UserNotification> {
    return this.subscriptionUserNotificationGQL.subscribe().pipe(
      mergeMap(({ data }) => {
        const notification = data?.userNotification;
        return notification ? of(notification) : EMPTY;
      })
    );
  }

  private processLiveNotification(notification: UserNotification): void {
    const existing = this.notificationMap.get(notification.notificationId);
    const statusChanged =
      existing !== undefined && existing.status !== notification.status;

    this.notificationMap.set(notification.notificationId, notification);

    if (existing && !statusChanged) {
      return;
    }

    this.handleNotification(notification);
  }

  private buildNotificationToastLines(
    notification: UserNotification
  ): NotificationToastLine[] {
    const payload = notification.payload;
    if (!payload || typeof payload !== 'object') {
      return [];
    }

    const record = payload as Record<string, unknown>;
    const total = record['total'];
    const successful = record['successful'];
    const failed = record['failed'];

    if (
      total === undefined ||
      successful === undefined ||
      failed === undefined
    ) {
      return [];
    }

    const lines: NotificationToastLine[] = [
      { text: `Total requested: ${total?.toString()}` },
      { text: `Succeeded: ${successful?.toString()}` },
    ];

    const failedLine: NotificationToastLine = {
      text: `Failed: ${failed?.toString()}`,
    };
    if (this.getInlineFailures(notification).length > 0) {
      failedLine.copyAction = {
        actionId: COPY_FAILED_IDS_ACTION_ID,
        tooltip: COPY_FAILED_IDS_TOOLTIP,
      };
    }

    lines.push(failedLine);
    return lines;
  }

  private shouldShowDownloadAction(notification: UserNotification): boolean {
    return (
      notification.action?.type ===
        SubscriptionNotificationActionType.DOWNLOAD &&
      !!notification.documentId &&
      (notification.status === SubscriptionNotificationStatus.COMPLETED ||
        notification.status ===
          SubscriptionNotificationStatus.COMPLETED_WITH_ERRORS)
    );
  }

  private getToastSeverity(
    status: SubscriptionNotificationStatus
  ): NotificationToastContent['severity'] {
    switch (status) {
      case SubscriptionNotificationStatus.COMPLETED:
        return 'success';
      case SubscriptionNotificationStatus.COMPLETED_WITH_ERRORS:
        return 'warn';
      case SubscriptionNotificationStatus.FAILED:
        return 'error';
      case SubscriptionNotificationStatus.SUBMITTED:
      case SubscriptionNotificationStatus.IN_PROGRESS:
      default:
        return 'info';
    }
  }

  executeAction(
    action: UserNotification['action'] | null | undefined,
    notification?: UserNotification
  ): void {
    if (!action?.type) {
      return;
    }

    switch (action.type) {
      case SubscriptionNotificationActionType.NAVIGATE:
      case SubscriptionNotificationActionType.VIEW_DETAILS:
        if (action.target) {
          void this.router.navigateByUrl(action.target);
        }
        break;
      case SubscriptionNotificationActionType.DOWNLOAD:
        if (!action.target || !notification?.documentId) {
          return;
        }

        this.fileHandlingService.downloadDocuments([
          {
            documentId: notification?.documentId,
            documentName: notification
              ? this.getPayloadString(notification.payload, 'fileName')
              : undefined,
          },
        ]);
        break;
    }
  }

  copyFailedLoanIds(notification: UserNotification): void {
    const failures = this.getInlineFailures(notification);
    if (failures.length === 0) {
      return;
    }

    const loanIds = failures
      .map((failure) => failure.loanId)
      .filter((loanId): loanId is string => !!loanId);

    if (loanIds.length === 0) {
      return;
    }

    void navigator.clipboard?.writeText(loanIds.join(', '));
  }

  private getInlineFailures(
    notification: UserNotification
  ): NotificationFailure[] {
    const payload = notification.payload;
    if (!payload || typeof payload !== 'object') {
      return [];
    }

    const failures = (payload as Record<string, unknown>)['failures'];
    if (!Array.isArray(failures)) {
      return [];
    }

    if (failures.length > MAX_INLINE_FAILURES) {
      return [];
    }

    return failures as NotificationFailure[];
  }

  private getPayloadString(
    payload: UserNotification['payload'],
    key: string
  ): string | undefined {
    if (!payload || typeof payload !== 'object') {
      return undefined;
    }

    const value = (payload as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : undefined;
  }
}
